(function () {
  'use strict';

  var bundleDisclosureExpanded;

  function titleTypeDelay(ch) {
    if (ch === ' ') return 120;
    if (ch === '—' || ch === '–' || ch === '-' || ch === ',' || ch === '.' || ch === ':' || ch === '!' || ch === '?') {
      return 96;
    }
    return 48 + Math.floor(Math.random() * 12);
  }

  function initDeskShot(root) {
    if (!root || root.getAttribute('data-desk-ready') === 'true') return;
    root.setAttribute('data-desk-ready', 'true');

    document.documentElement.classList.add('desk-shot-pdp');
    hideOrphanAddons();

    setupTitleType(root);
    setupPriceMarkup(root);
    var gallery = setupShots(root);
    setupFullscreen(root, gallery);
    setupHeroCounter(root, gallery);
    setupShotsNav(root, gallery);
    setupHotkeys(root, gallery);
    setupQty(root);
    setupBundleDisclosure(root);
    setupVariantSelects(root, gallery);
    setupSticky(root);
    setupEdgeRise(root);
  }

  function setupTitleType(root) {
    var title = root.querySelector('[data-desk-type-title]');
    if (!title) return;
    var text = title.getAttribute('data-desk-type-title') || '';
    var out = title.querySelector('[data-desk-type-out]');
    if (!out || !text) return;

    var reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Always type on every PDP load (unless reduced motion). A sessionStorage
       "already typed" flag made the effect feel broken — same product / back
       nav / soft reloads skipped the type-in and just flashed the full H1. */
    if (reduceMotion) {
      out.textContent = text;
      title.classList.remove('is-typing');
      title.classList.add('is-typed');
      return;
    }

    /* Clear immediately so SSR text cannot paint; caret shows while we start. */
    out.textContent = '';
    title.classList.remove('is-typed');
    title.classList.add('is-typing');

    var reserveHeight = function () {
      if (!title.isConnected) return;
      var probe = document.createElement('span');
      probe.setAttribute('aria-hidden', 'true');
      probe.textContent = text;
      probe.style.cssText =
        'position:absolute;left:0;right:0;top:0;visibility:hidden;pointer-events:none;display:block;';
      var prevPos = title.style.position;
      if (!prevPos) title.style.position = 'relative';
      title.appendChild(probe);
      var h = probe.getBoundingClientRect().height;
      title.removeChild(probe);
      if (!prevPos) title.style.position = '';
      if (h > 0) title.style.minHeight = h + 'px';
    };

    var i = 0;
    var step = function () {
      if (!title.isConnected) return;
      i += 1;
      out.textContent = text.slice(0, i);
      if (i >= text.length) {
        title.classList.remove('is-typing');
        title.classList.add('is-typed');
        title.style.minHeight = '';
        return;
      }
      var ch = text.charAt(i - 1);
      /* Cadence (Aaron 2026-08-02: "type in a little slower and cleaner
         looking"). Was `28 + random(0..32)` per character: a 32ms spread on a
         28ms base meant one character could take more than twice as long as
         the next, which read as jitter rather than as deliberate typing. The
         spread is now 12ms on a slower base, so the rhythm is even, and the
         longer beats land on word and punctuation boundaries where the eye
         already expects a pause — the stutter tracks the words instead of
         happening mid-word. */
      window.setTimeout(step, titleTypeDelay(ch));
    };

    /* Reserve height when the face is ready (or quickly time out), but do not
       gate the type-in on fonts.ready — that race often skipped/stalled typing. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(reserveHeight).catch(function () {});
    }
    /* Keep the hero title on ONE line wherever it can be (Aaron 2026-08-02:
       "the main hero titles should not line break if possible"). CSS alone
       cannot do this — it has no way to measure a string — so the size is
       trimmed to fit here.

       The wrap was never really about the type scale: .desk-shot__hero-copy
       capped at 800px while "Programmed to Wilt" needs 880px at the 108px
       ceiling, inside a hero 1320px wide. The cap is widened in CSS; this is
       the guard for titles still too long at that width, and it scales down to
       a floor rather than letting a very long name shrink to nothing.

       Measures the FULL title from the data attribute, not the partially typed
       text, and runs before reserveHeight so the reserved height reflects the
       final size. */
    /* Floor for the shrink-to-fit. 46px was too high to be useful on phones:
       a 21-character title in a 350px column needs ~35px, so the guard bailed
       out and let it wrap — which is exactly what it exists to prevent. 32px
       still reads as a hero on a 390px screen, and titles long enough to need
       less than that are genuinely better wrapped. */
    var MIN_TITLE_PX = 32;

    var fitTitle = function () {
      if (!title.isConnected) return;
      title.style.fontSize = '';
      var avail = title.clientWidth;
      if (!avail) return;
      var cs = window.getComputedStyle(title);
      var base = parseFloat(cs.fontSize) || 0;
      if (!base) return;
      /* The caret is a real inline box that sits after the text and stays in
         the flow when typing finishes, so it has to come out of the budget.
         Measuring text alone is what made the title write out on one line and
         then snap to two at the last character (Aaron 2026-08-02) — the string
         fitted, the string PLUS the caret did not. A small proportional margin
         on top absorbs sub-pixel and font-swap drift, which is the other way a
         "just fits" measurement turns into a wrap after webfonts settle. */
      var caret = title.querySelector('.desk-shot__title-caret');
      var caretAllow = 0;
      if (caret) {
        var caretCS = window.getComputedStyle(caret);
        caretAllow =
          caret.getBoundingClientRect().width +
          (parseFloat(caretCS.marginLeft) || 0) +
          (parseFloat(caretCS.marginRight) || 0);
      }
      avail = avail - caretAllow - base * 0.04;
      if (avail <= 0) return;

      var probe = document.createElement('span');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;pointer-events:none;';
      probe.style.font = cs.font;
      probe.style.fontFamily = cs.fontFamily;
      probe.style.fontWeight = cs.fontWeight;
      probe.style.fontSize = cs.fontSize;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.textTransform = cs.textTransform;
      probe.textContent = text;
      document.body.appendChild(probe);
      var natural = probe.getBoundingClientRect().width;
      probe.remove();
      if (natural > avail && natural > 0) {
        var scaled = Math.floor(base * (avail / natural));
        /* Below the floor the title is long enough that wrapping reads better
           than shrinking — "if possible" was the brief, not "at any cost". */
        if (scaled >= MIN_TITLE_PX) title.style.fontSize = scaled + 'px';
      }
    };

    var refit = function () {
      fitTitle();
      reserveHeight();
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refit).catch(function () {});
    }
    var refitTimer = null;
    window.addEventListener('resize', function () {
      if (refitTimer) window.clearTimeout(refitTimer);
      refitTimer = window.setTimeout(refit, 150);
    });

    window.setTimeout(refit, 0);
    window.setTimeout(step, 100);
  }

  function hideOrphanAddons() {
    var needles = ['add selected to cart', 'add selected'];
    var nodes = document.body ? document.body.querySelectorAll('div, section, form, aside') : [];
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.getAttribute('data-desk-io-hide') === 'true') return;
      if (el.closest && el.closest('[data-desk-shot-root]')) return;
      if (el.closest && el.closest('.jdgm-rev-widg, .jdgm-widget, #judgeme_product_reviews')) return;
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (!text || text.length > 600) return;
      var hit = needles.some(function (n) {
        return text.indexOf(n) !== -1;
      });
      if (!hit) return;
      el.setAttribute('data-desk-io-hide', 'true');
      el.style.setProperty('display', 'none', 'important');
    });
  }

  function setupShots(root) {
    var shots = Array.from(root.querySelectorAll('[data-desk-shot]'));
    var shotsTrack = root.querySelector('[data-desk-shots-track]');
    var stickyThumb = root.querySelector('[data-desk-sticky-thumb]');
    var hero = root.querySelector('.desk-shot__hero');
    var heroMedia = root.querySelector('[data-desk-hero-media]');
    var heroImg = heroMedia ? heroMedia.querySelector('img') : null;
    var heroBottom = root.querySelector('[data-desk-hero-bottom]');
    var heroShade = root.querySelector('.desk-shot__hero-shade');
    var heroMeta = root.querySelector('.desk-shot__meta');
    var mobilePurchase = root.querySelector('.desk-shot__mobile-purchase');
    var mobileMetaSlot = root.querySelector('[data-desk-mobile-meta-slot]');
    var mobilePurchaseSlot = root.querySelector('[data-desk-mobile-purchase-slot]');
    var mobilePurchaseSlotBelow = root.querySelector('[data-desk-mobile-purchase-slot-below]');
    var variantsBlock = root.querySelector('.desk-shot__variants');
    var variantsMobileSlot = root.querySelector('[data-desk-variants-slot]');
    var companionBlock = root.querySelector('.desk-shot__companion');
    var companionMobileSlot = root.querySelector('[data-desk-companion-slot]');
    var prevBtn = root.querySelector('[data-desk-hero-prev]');
    var nextBtn = root.querySelector('[data-desk-hero-next]');
    var fsOpenBtn = root.querySelector('[data-desk-hero-fs-open]');
    var index = 0;
    var listeners = [];
    var ready = false;
    var galleryEngaged = false;
    var autoTimer = null;
    var autoPaused = false;
    var transientPaused = false;
    var AUTO_MS = 4500;
    var heroFront = heroImg;
    var heroBack = null;
    var swapToken = 0;
    var preloadCache = {};
    var reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var toggleBtn = root.querySelector('[data-desk-gallery-toggle]');
    var toggleLabel = toggleBtn ? toggleBtn.querySelector('[data-desk-gallery-toggle-label]') : null;
    var videoToggleBtn = root.querySelector('[data-desk-video-toggle]');
    var videoToggleLabel = videoToggleBtn
      ? videoToggleBtn.querySelector('[data-desk-video-toggle-label]')
      : null;
    var cleanStillElements = hero
      ? Array.from(
          hero.querySelectorAll(
            '.desk-shot__video-toggle, .desk-shot__artist-pill, .desk-shot__le-rail'
          )
        )
      : [];
    var purchaseTitle = root.querySelector('[data-desk-purchase-title]');
    var purchaseTitleText = purchaseTitle
      ? purchaseTitle.getAttribute('data-desk-purchase-type-title') || ''
      : '';
    var purchaseTitleOut = purchaseTitle
      ? purchaseTitle.querySelector('[data-desk-purchase-type-out]')
      : null;
    var purchaseTitleTyped = false;
    var purchaseTitleTimer = null;
    var pauseStorageKey = 'epicdesk:desk-shot-gallery-paused';

    try {
      autoPaused = reduceMotion || window.sessionStorage.getItem(pauseStorageKey) === 'true';
    } catch (e) {
      autoPaused = reduceMotion;
    }

    /* Product videos (muted, looping) live as extra layers in the same stack as
       the image crossfade pair and are switched with the same .is-front class.
       When a video leads, the image layers must NOT open in front of it. */
    var heroVideos = heroMedia
      ? Array.from(heroMedia.querySelectorAll('[data-desk-hero-video]'))
      : [];
    var videoLeads = heroVideos.length > 0;

    /* Shopify can expose several MP4 renditions for one product video. Keep
       every URL inert in data-src, then materialize exactly one rendition at
       first play. This avoids the old all-source load() path, which let the
       browser probe/download multiple resolutions before choosing. */
    function selectVideoSource(vid) {
      var candidates = Array.from(vid.querySelectorAll('source[data-src]'));
      if (!candidates.length) return null;

      var cssWidth = (heroMedia && heroMedia.clientWidth) || window.innerWidth || 1280;
      var dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
      var isMobile =
        typeof window.matchMedia === 'function'
          ? window.matchMedia('(max-width: 749px)').matches
          : cssWidth <= 749;
      var targetWidth = Math.min(Math.ceil(cssWidth * dpr), isMobile ? 960 : 1920);
      var measured = candidates
        .map(function (source) {
          return {
            source: source,
            width: parseInt(source.getAttribute('data-width'), 10) || 0,
          };
        })
        .filter(function (candidate) {
          return candidate.width > 0;
        })
        .sort(function (a, b) {
          return a.width - b.width;
        });

      if (!measured.length) return candidates[0];
      var selected = measured.find(function (candidate) {
        return candidate.width >= targetWidth;
      });
      return (selected || measured[measured.length - 1]).source;
    }

    function materializeVideoSources(vid) {
      if (!vid || vid.getAttribute('data-desk-video-ready') === 'true') return;
      var sources = Array.from(vid.querySelectorAll('source[data-src]'));
      var selected = selectVideoSource(vid);
      var src = selected ? selected.getAttribute('data-src') : '';

      vid.setAttribute('data-desk-video-source-count', String(sources.length));
      if (selected) {
        vid.setAttribute('data-desk-video-selected-width', selected.getAttribute('data-width') || '');
        vid.setAttribute('data-desk-video-selected-height', selected.getAttribute('data-height') || '');
      }
      sources.forEach(function (source) {
        if (source.parentNode) source.parentNode.removeChild(source);
      });
      if (src) vid.src = src;
      vid.setAttribute('data-desk-video-ready', 'true');
      if (src) vid.load();
    }

    function updateVideoControl(vid) {
      if (!videoToggleBtn) return;
      var isActive = !!(vid && vid.classList.contains('is-front'));
      videoToggleBtn.hidden = !isActive;
      if (!isActive) {
        videoToggleBtn.removeAttribute('aria-controls');
        return;
      }

      var paused = vid.paused;
      var playLabel = videoToggleBtn.getAttribute('data-label-play') || 'Play product video';
      var pauseLabel = videoToggleBtn.getAttribute('data-label-pause') || 'Pause product video';
      var label = paused ? playLabel : pauseLabel;
      videoToggleBtn.setAttribute('data-state', paused ? 'paused' : 'playing');
      videoToggleBtn.setAttribute('aria-label', label);
      if (vid.id) videoToggleBtn.setAttribute('aria-controls', vid.id);
      if (videoToggleLabel) videoToggleLabel.textContent = label;
    }

    function pauseVideo(vid, userInitiated) {
      if (!vid) return;
      if (userInitiated) vid.setAttribute('data-desk-video-user-paused', 'true');
      vid.pause();
      updateVideoControl(vid);
    }

    function playVideo(vid, userInitiated) {
      if (!vid) return;
      if (
        !userInitiated &&
        (reduceMotion || vid.getAttribute('data-desk-video-user-paused') === 'true')
      ) {
        pauseVideo(vid, false);
        return;
      }

      materializeVideoSources(vid);
      vid.setAttribute('data-desk-video-activated', 'true');
      if (userInitiated) vid.setAttribute('data-desk-video-user-paused', 'false');
      vid.muted = true; /* re-assert: autoplay is refused if this is ever false */
      var promise;
      try {
        promise = vid.play();
      } catch (e) {
        updateVideoControl(vid);
        return;
      }
      updateVideoControl(vid);
      if (promise && typeof promise.then === 'function') {
        promise.then(function () {
          updateVideoControl(vid);
        }).catch(function () {
          updateVideoControl(vid);
        });
      }
    }

    function showVideo(videoIndex, allowPlayback) {
      heroVideos.forEach(function (vid, i) {
        var on = i === videoIndex;
        vid.classList.toggle('is-front', on);
        if (
          on &&
          allowPlayback &&
          !reduceMotion &&
          vid.getAttribute('data-desk-video-user-paused') !== 'true'
        ) {
          if (!vid.hasAttribute('data-desk-video-user-paused')) {
            vid.setAttribute('data-desk-video-user-paused', 'false');
          }
          playVideo(vid, false);
        } else {
          pauseVideo(vid, false);
        }
      });
      if (heroFront) heroFront.classList.remove('is-front');
      if (heroBack) heroBack.classList.remove('is-front');
      updateVideoControl(heroVideos[videoIndex] || null);
    }

    function hideVideos() {
      heroVideos.forEach(function (vid) {
        vid.classList.remove('is-front');
        pauseVideo(vid, false);
      });
      updateVideoControl(null);
    }

    heroVideos.forEach(function (vid) {
      ['play', 'pause', 'ended'].forEach(function (eventName) {
        vid.addEventListener(eventName, function () {
          if (vid.classList.contains('is-front')) updateVideoControl(vid);
        });
      });
    });

    if (videoToggleBtn) {
      videoToggleBtn.addEventListener('click', function () {
        var vid = heroVideos.find(function (candidate) {
          return candidate.classList.contains('is-front');
        });
        if (!vid) return;
        pauseAutoplay(true);
        if (vid.paused) playVideo(vid, true);
        else pauseVideo(vid, true);
      });
    }

    /* Stop decoding once the hero is off screen. An autoplaying loop that keeps
       running while the shopper reads the specs burns battery and bandwidth for
       something nobody can see; this hands both back and resumes on return.
       Only the layer that is actually in front is ever resumed. */
    if (heroVideos.length && heroMedia && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            heroVideos.forEach(function (vid) {
              if (!vid.classList.contains('is-front')) return;
              if (
                entry.isIntersecting &&
                !reduceMotion &&
                vid.getAttribute('data-desk-video-activated') === 'true' &&
                vid.getAttribute('data-desk-video-user-paused') !== 'true'
              ) {
                playVideo(vid, false);
              } else {
                pauseVideo(vid, false);
              }
            });
          });
        },
        { threshold: 0.15 }
      ).observe(heroMedia);
    }

    if (heroMedia && heroImg) {
      heroMedia.classList.add('desk-shot__hero-media--layered');
      heroImg.classList.add('desk-shot__hero-layer');
      if (!videoLeads) heroImg.classList.add('is-front');
      heroBack = heroImg.cloneNode(true);
      heroBack.classList.add('desk-shot__hero-layer');
      heroBack.classList.remove('is-front');
      heroBack.removeAttribute('fetchpriority');
      heroBack.setAttribute('aria-hidden', 'true');
      heroBack.alt = '';
      heroMedia.appendChild(heroBack);
      heroFront = heroImg;
    }

    function notify() {
      listeners.forEach(function (fn) {
        try {
          fn(index);
        } catch (e) {
          /* ignore */
        }
      });
    }

    /* Once a shopper changes to a still, leave the artwork completely clean.
       The price and Add to cart stay visible; thumbnails and swipe remain
       available. A video exits clean mode and exposes only its Play/Pause. */
    function cleanStillHides(element) {
      if (!element) return false;
      return cleanStillElements.some(function (target) {
        return target === element || target.contains(element);
      });
    }

    function finishPurchaseTitle() {
      if (!purchaseTitle || !purchaseTitleOut) return;
      if (purchaseTitleTimer !== null) {
        window.clearTimeout(purchaseTitleTimer);
        purchaseTitleTimer = null;
      }
      purchaseTitleOut.textContent = purchaseTitleText;
      purchaseTitle.classList.remove('is-typing');
      purchaseTitle.classList.add('is-typed');
      purchaseTitle.style.minHeight = '';
    }

    function reservePurchaseTitleHeight() {
      if (!purchaseTitle || !purchaseTitleText || !purchaseTitle.isConnected) return;
      var probe = document.createElement('span');
      probe.setAttribute('aria-hidden', 'true');
      probe.textContent = purchaseTitleText;
      probe.style.cssText =
        'position:absolute;left:0;right:0;top:0;visibility:hidden;pointer-events:none;display:block;';
      var previousPosition = purchaseTitle.style.position;
      if (!previousPosition) purchaseTitle.style.position = 'relative';
      purchaseTitle.appendChild(probe);
      var height = probe.getBoundingClientRect().height;
      purchaseTitle.removeChild(probe);
      if (!previousPosition) purchaseTitle.style.position = '';
      if (height > 0) purchaseTitle.style.minHeight = height + 'px';
    }

    function typePurchaseTitleOnce() {
      if (!purchaseTitle || !purchaseTitleOut || !purchaseTitleText) return;
      if (purchaseTitleTyped || reduceMotion) {
        purchaseTitleTyped = true;
        finishPurchaseTitle();
        return;
      }

      purchaseTitleTyped = true;
      reservePurchaseTitleHeight();
      purchaseTitleOut.textContent = '';
      purchaseTitle.classList.remove('is-typed');
      purchaseTitle.classList.add('is-typing');
      var index = 0;
      var step = function () {
        if (!purchaseTitle.isConnected) return;
        index += 1;
        purchaseTitleOut.textContent = purchaseTitleText.slice(0, index);
        if (index >= purchaseTitleText.length) {
          purchaseTitleTimer = null;
          finishPurchaseTitle();
          return;
        }
        purchaseTitleTimer = window.setTimeout(step, titleTypeDelay(purchaseTitleText.charAt(index - 1)));
      };
      purchaseTitleTimer = window.setTimeout(step, 100);
    }

    function setCleanStill(on) {
      root.classList.toggle('is-photo-viewing', on);
      if (hero) hero.classList.toggle('is-photo-viewing', on);
      if (heroShade) heroShade.classList.toggle('is-hidden', on);
      cleanStillElements.forEach(function (element) {
        if (on) {
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
        } else {
          element.removeAttribute('inert');
          element.removeAttribute('aria-hidden');
        }
      });
      /* The purchase band lives below the hero at every width; its title
         types once on the first call (init) and stays visible in every mode. */
      typePurchaseTitleOnce();
    }

    function moveAfter(node, anchor) {
      if (!node || !anchor || !anchor.parentNode) return;
      anchor.parentNode.insertBefore(node, anchor.nextSibling);
    }

    function syncResponsiveLayout() {
      var focusBeforeMove = document.activeElement;
      var preserveFocus = Boolean(
        focusBeforeMove &&
        focusBeforeMove !== document.body &&
        ((heroMeta && heroMeta.contains(focusBeforeMove)) ||
          (mobilePurchase && mobilePurchase.contains(focusBeforeMove)) ||
          (variantsBlock && variantsBlock.contains(focusBeforeMove)) ||
          (companionBlock && companionBlock.contains(focusBeforeMove)))
      );

      /* Universal purchase band: panel, meta, variants, and companion nodes
         relocate below the hero at EVERY width (was gated to <=989px). Meta
         moves first, into the panel's own slot, then the panel moves up. */
      moveAfter(heroMeta, mobileMetaSlot);
      /* <=989px: the band sits BELOW the shot strip (hero -> thumbnails ->
         purchase); >=990px keeps the slot above the strip (Trey 2026-09-01). */
      var bandBelowStrip =
        Boolean(mobilePurchaseSlotBelow) &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 989px)').matches;
      moveAfter(mobilePurchase, bandBelowStrip ? mobilePurchaseSlotBelow : mobilePurchaseSlot);
      moveAfter(variantsBlock, variantsMobileSlot);
      moveAfter(companionBlock, companionMobileSlot);

      setCleanStill(root.classList.contains('is-photo-viewing'));

      if (preserveFocus && focusBeforeMove && focusBeforeMove.isConnected) {
        try {
          focusBeforeMove.focus({ preventScroll: true });
        } catch (e) {
          focusBeforeMove.focus();
        }
      }
    }

    syncResponsiveLayout();

    /* The band's slot depends on the breakpoint, so re-sync when it flips
       (device rotation, window resize). Focus is preserved by the function. */
    if (typeof window.matchMedia === 'function') {
      var bandBreakpoint = window.matchMedia('(max-width: 989px)');
      var onBandBreakpoint = function () { syncResponsiveLayout(); };
      if (bandBreakpoint.addEventListener) bandBreakpoint.addEventListener('change', onBandBreakpoint);
      else if (bandBreakpoint.addListener) bandBreakpoint.addListener(onBandBreakpoint);
    }

    function stopAutoplay() {
      if (autoTimer) {
        clearTimeout(autoTimer);
        autoTimer = null;
      }
    }

    function scheduleAutoplay() {
      stopAutoplay();
      /* Autoplay retired 2026-08-24 (gallery must not fight the shopper). It
         was already structurally inert — the [data-desk-gallery-toggle] button
         that arms the guard below is not rendered anywhere — this return makes
         the retirement explicit instead of relying on a missing button.
         pauseAutoplay/resumeAutoplay/pauseTransient/resumeTransient stay as
         safe no-ops (activate, the video toggle, and the fullscreen dialog
         still call them). Revert: delete this return. */
      return;
      if (!toggleBtn || autoPaused || transientPaused || shots.length < 2) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      autoTimer = window.setTimeout(function () {
        autoTimer = null;
        activate(index + 1, { auto: true });
        scheduleAutoplay();
      }, AUTO_MS);
    }

    function updateAutoplayControl() {
      if (!toggleBtn) return;
      var playLabel = toggleBtn.getAttribute('data-label-play') || 'Play slideshow';
      var pauseLabel = toggleBtn.getAttribute('data-label-pause') || 'Pause slideshow';
      var label = autoPaused ? playLabel : pauseLabel;
      toggleBtn.setAttribute('aria-pressed', autoPaused ? 'true' : 'false');
      toggleBtn.setAttribute('aria-label', label);
      toggleBtn.setAttribute('data-state', autoPaused ? 'paused' : 'playing');
      if (toggleLabel) toggleLabel.textContent = label;
    }

    function persistAutoplayState() {
      try {
        window.sessionStorage.setItem(pauseStorageKey, autoPaused ? 'true' : 'false');
      } catch (e) {
        /* Storage can be unavailable in privacy modes; the current page still works. */
      }
    }

    function pauseAutoplay(persist) {
      autoPaused = true;
      stopAutoplay();
      if (persist) persistAutoplayState();
      updateAutoplayControl();
    }

    function resumeAutoplay(persist) {
      autoPaused = false;
      if (persist) persistAutoplayState();
      updateAutoplayControl();
      scheduleAutoplay();
    }

    function pauseTransient() {
      transientPaused = true;
      stopAutoplay();
    }

    function resumeTransient() {
      transientPaused = false;
      scheduleAutoplay();
    }

    if (toggleBtn) {
      updateAutoplayControl();
      toggleBtn.addEventListener('click', function () {
        if (autoPaused) resumeAutoplay(true);
        else pauseAutoplay(true);
      });
    }

    function setAutoFade(on) {
      if (!heroMedia) return;
      heroMedia.classList.toggle('is-auto-fade', !!on);
    }

    function preloadSrc(src, srcset) {
      if (!src || preloadCache[src]) return preloadCache[src];
      var img = new Image();
      img.decoding = 'async';
      img.sizes = '100vw';
      if (srcset) img.srcset = srcset;
      img.src = src;
      preloadCache[src] = img;
      return img;
    }

    function preloadNeighbors(center) {
      if (!shots.length) return;
      var prev = nearestVisible(center - 1, -1);
      var next = nearestVisible(center + 1, 1);
      [prev, center, next].forEach(function (i) {
        var shot = shots[i];
        if (!shot) return;
        preloadSrc(shot.getAttribute('data-full-src'), shot.getAttribute('data-full-srcset'));
      });
    }

    function applyToLayer(layer, src, srcset, alt) {
      if (!layer || !src) return;
      if (srcset) layer.srcset = srcset;
      else layer.removeAttribute('srcset');
      layer.sizes = '100vw';
      layer.src = src;
      if (alt != null) layer.alt = alt;
    }

    function showLayer(layer) {
      if (!layer || !heroFront) return;
      if (layer === heroFront) {
        layer.classList.add('is-front');
        if (heroBack) heroBack.classList.remove('is-front');
        return;
      }
      layer.classList.add('is-front');
      heroFront.classList.remove('is-front');
      heroBack = heroFront;
      heroFront = layer;
      heroImg = heroFront;
    }

    function paintHero(fullSrc, fullSrcset, activeShot) {
      if (!heroFront || !fullSrc) return;

      var token = ++swapToken;
      var stripImg = activeShot ? activeShot.querySelector('img') : null;
      var instantSrc = stripImg && (stripImg.currentSrc || stripImg.src);
      var incoming = heroBack || heroFront;
      var cached = preloadCache[fullSrc];
      var readyNow = cached && cached.complete && cached.naturalWidth > 0;

      // Instant preview from strip thumb while full hero decodes
      if (!readyNow && instantSrc && incoming) {
        applyToLayer(incoming, instantSrc, null, activeShot.getAttribute('aria-label') || '');
        showLayer(incoming);
      }

      var probe = cached || preloadSrc(fullSrc, fullSrcset);

      function commitFull() {
        if (token !== swapToken) return;
        var target = heroBack && heroBack !== heroFront ? heroBack : heroFront;
        // If we already showed instant on back and swapped, paint full onto the non-front then swap
        if (heroBack && heroFront) {
          target = heroFront.classList.contains('is-front') ? heroBack : heroFront;
        }
        applyToLayer(target, fullSrc, fullSrcset, activeShot ? activeShot.getAttribute('aria-label') || '' : '');
        var finish = function () {
          if (token !== swapToken) return;
          showLayer(target);
        };
        if (typeof target.decode === 'function') {
          target.decode().then(finish).catch(finish);
        } else if (target.complete) {
          finish();
        } else {
          target.onload = finish;
        }
      }

      if (readyNow || probe.complete) {
        commitFull();
      } else {
        probe.onload = commitFull;
        // Also kick network immediately on the layer
        if (incoming && !instantSrc) {
          applyToLayer(incoming, fullSrc, fullSrcset, activeShot ? activeShot.getAttribute('aria-label') || '' : '');
        }
      }
    }

    /* Keep the active tile visible in the strip. Horizontal-only container
       scroll: scrollIntoView can nudge the page vertically. */
    function followShotsTrack(active) {
      if (!shotsTrack || !active) return;
      var trackLeft = shotsTrack.scrollLeft;
      var activeLeft = active.offsetLeft;
      var activeRight = activeLeft + active.offsetWidth;
      var viewRight = trackLeft + shotsTrack.clientWidth;
      var nextLeft = trackLeft;
      if (activeLeft < trackLeft) nextLeft = activeLeft;
      else if (activeRight > viewRight) nextLeft = activeRight - shotsTrack.clientWidth;
      if (nextLeft === trackLeft) return;
      if (typeof shotsTrack.scrollTo === 'function') {
        shotsTrack.scrollTo({
          left: nextLeft,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      } else {
        shotsTrack.scrollLeft = nextLeft;
      }
    }

    /* —— Variant colour filter —— */
    function shotVisibleAt(i) {
      var shot = shots[i];
      return !!shot && !shot.classList.contains('is-filtered-out');
    }

    function firstVisibleIndex() {
      for (var i = 0; i < shots.length; i += 1) {
        if (shotVisibleAt(i)) return i;
      }
      return 0;
    }

    function visibleShotCount() {
      var n = 0;
      for (var i = 0; i < shots.length; i += 1) {
        if (shotVisibleAt(i)) n += 1;
      }
      return n;
    }

    function visiblePositionOf(target) {
      var pos = 0;
      for (var i = 0; i < target && i < shots.length; i += 1) {
        if (shotVisibleAt(i)) pos += 1;
      }
      return pos;
    }

    function nearestVisible(from, dir) {
      if (!shots.length) return from;
      var i = ((from % shots.length) + shots.length) % shots.length;
      for (var guard = 0; guard < shots.length; guard += 1) {
        if (shotVisibleAt(i)) return i;
        i = (((i + dir) % shots.length) + shots.length) % shots.length;
      }
      return from;
    }

    /* token: 'black'|'navy'|'white'|'off-white' or null; variantId: current
       variant id as a string. A tile stays visible when it is untagged
       (neutral), carries the current variant id, or carries the colour token.
       If NO tagged tile matches the request the filter clears entirely — a
       neutral-only rump gallery helps nobody. */
    function setColorFilter(token, variantId) {
      if (!shots.length) return;
      var anyTaggedMatch = false;
      var want = shots.map(function (shot) {
        var color = shot.getAttribute('data-shot-color') || '';
        var ids = shot.getAttribute('data-shot-variant') || '';
        if (!token || (!color && !ids)) return true;
        var hit =
          (variantId && ids.split(',').indexOf(variantId) !== -1) ||
          (color && color === token);
        if (hit) anyTaggedMatch = true;
        return hit;
      });
      if (!token || !anyTaggedMatch) {
        want = shots.map(function () {
          return true;
        });
      }
      var changed = false;
      shots.forEach(function (shot, i) {
        var hide = !want[i];
        if (shot.classList.contains('is-filtered-out') !== hide) changed = true;
        shot.classList.toggle('is-filtered-out', hide);
      });
      if (!changed) return;
      if (!shotVisibleAt(index)) {
        activate(firstVisibleIndex(), { silent: true });
      } else {
        notify();
      }
    }

    function activate(nextIndex, opts) {
      opts = opts || {};
      if (!shots.length) {
        notify();
        return;
      }
      var prev = index;
      index = ((nextIndex % shots.length) + shots.length) % shots.length;
      /* Filtered tiles are not navigable: step in the direction of travel
         until a visible tile is found (arrows/hotkeys/swipe/fullscreen pass
         ±1; strip clicks and variant sync always target visible tiles). */
      if (!shotVisibleAt(index)) {
        index = nearestVisible(index, nextIndex >= prev ? 1 : -1);
      }
      var userAction = !opts.auto && !opts.silent && ready;

      if (userAction) pauseAutoplay(true);

      shots.forEach(function (shot, i) {
        var on = i === index;
        shot.classList.toggle('is-active', on);
        shot.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var active = shots[index];
      if (!active) return;

      var fullSrc = active.getAttribute('data-full-src');
      var fullSrcset = active.getAttribute('data-full-srcset');
      var thumbSrc = active.getAttribute('data-thumb-src');
      var isVideoShot = active.getAttribute('data-media-type') === 'video';

      setAutoFade(!!opts.auto);

      if (isVideoShot) {
        /* Video shots carry no data-full-src — bring their layer forward
           instead of painting into the image crossfade pair. */
        showVideo(
          parseInt(active.getAttribute('data-video-index'), 10) || 0,
          userAction && !opts.fullscreen
        );
      } else {
        hideVideos();
        if (fullSrc) {
          paintHero(fullSrc, fullSrcset, active);
        }
      }

      /* The sticky bar holds the product render and does not follow the gallery
         (data-desk-sticky-thumb-static). Swapping it per shot made the thumb
         flicker through lifestyle photos while the price sat still beside it. */
      if (stickyThumb && thumbSrc && !stickyThumb.hasAttribute('data-desk-sticky-thumb-static')) {
        var stickyImg = stickyThumb.querySelector('img');
        if (stickyImg) {
          stickyImg.src = thumbSrc;
          stickyImg.removeAttribute('srcset');
        }
      }

      /* Every change follows — arrows, keyboard, strip clicks, fullscreen nav
         (was gated on !opts.auto; unconditional keeps it correct if autoplay
         is ever revived). */
      followShotsTrack(active);

      preloadNeighbors(index);

      if (userAction && index !== prev) galleryEngaged = true;
      var cleanStill = galleryEngaged && !isVideoShot;
      var focusBeforeClean = document.activeElement;
      setCleanStill(cleanStill);

      /* Keyboard activation must not strand focus on a control that clean mode
         just hid. The active thumbnail is the visible navigation equivalent. */
      if (
        cleanStill &&
        !opts.fullscreen &&
        cleanStillHides(focusBeforeClean)
      ) {
        try {
          active.focus({ preventScroll: true });
        } catch (e) {
          active.focus();
        }
      }

      notify();
    }

    if (shots.length) {
      shots.forEach(function (shot, i) {
        shot.addEventListener('click', function () {
          activate(i);
        });
        // Warm on hover/focus for even snappier clicks
        shot.addEventListener('pointerenter', function () {
          preloadSrc(shot.getAttribute('data-full-src'), shot.getAttribute('data-full-srcset'));
        });
        shot.addEventListener('focus', function () {
          preloadSrc(shot.getAttribute('data-full-src'), shot.getAttribute('data-full-srcset'));
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener('click', function (event) {
          event.preventDefault();
          activate(index - 1);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function (event) {
          event.preventDefault();
          activate(index + 1);
        });
      }

      var swipeSurface = hero || heroMedia;
      if (swipeSurface && shots.length > 1) {
        var startX = 0;
        var startY = 0;
        var swipePointerId = null;
        var tracking = false;
        var swipeBlockSelector =
          'a, button, input, select, textarea, label, [role="button"], [contenteditable="true"]';

        swipeSurface.addEventListener(
          'pointerdown',
          function (event) {
            if (event.isPrimary === false) return;
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (event.target && event.target.closest && event.target.closest(swipeBlockSelector)) {
              return;
            }
            tracking = true;
            swipePointerId = event.pointerId;
            startX = event.clientX;
            startY = event.clientY;
            try {
              swipeSurface.setPointerCapture(event.pointerId);
            } catch (e) {
              /* ignore */
            }
          },
          { passive: true }
        );

        swipeSurface.addEventListener('pointerup', function (event) {
          if (!tracking || event.pointerId !== swipePointerId) return;
          tracking = false;
          swipePointerId = null;
          var dx = event.clientX - startX;
          var dy = event.clientY - startY;
          var minSwipe = Math.max(36, Math.min(56, swipeSurface.clientWidth * 0.08));
          if (Math.abs(dx) < minSwipe || Math.abs(dx) < Math.abs(dy) * 1.1) return;
          if (dx < 0) activate(index + 1);
          else activate(index - 1);
        });

        swipeSurface.addEventListener('pointercancel', function (event) {
          if (swipePointerId !== null && event.pointerId !== swipePointerId) return;
          tracking = false;
          swipePointerId = null;
        });
      }

      var initial = shots.findIndex(function (shot) {
        return shot.classList.contains('is-active');
      });
      activate(initial >= 0 ? initial : 0, { silent: true });
      ready = true;
      scheduleAutoplay();

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stopAutoplay();
        else if (!autoPaused && !transientPaused) scheduleAutoplay();
      });
    }

    if (reduceMotion && heroBottom) {
      heroBottom.setAttribute('data-reduce-motion', 'true');
    }

    return {
      count: shots.length,
      activate: activate,
      pauseAutoplay: pauseAutoplay,
      resumeAutoplay: resumeAutoplay,
      pauseTransient: pauseTransient,
      resumeTransient: resumeTransient,
      isPaused: function () {
        return autoPaused;
      },
      isCleanTarget: function (element) {
        return Boolean(hero && hero.classList.contains('is-photo-viewing')) &&
          cleanStillHides(element);
      },
      getIndex: function () {
        return index;
      },
      setColorFilter: setColorFilter,
      isShotVisible: shotVisibleAt,
      getVisibleCount: visibleShotCount,
      getVisiblePosition: function () {
        return visiblePositionOf(index);
      },
      getFullscreenMedia: function () {
        var activeShot = shots[index];
        if (activeShot && activeShot.getAttribute('data-media-type') === 'video') {
          var videoIndex = parseInt(activeShot.getAttribute('data-video-index'), 10) || 0;
          var activeVideo = heroVideos[videoIndex] || null;
          var poster = activeVideo
            ? activeVideo.poster || activeVideo.getAttribute('poster')
            : activeShot.getAttribute('data-thumb-src');
          if (!poster) return null;
          return {
            src: poster,
            srcset: '',
            alt: activeVideo
              ? activeVideo.getAttribute('data-desk-video-poster-alt') ||
                activeVideo.getAttribute('aria-label') ||
                activeShot.getAttribute('aria-label') ||
                'Product video preview'
              : activeShot.getAttribute('aria-label') || 'Product video preview',
          };
        }
        if (activeShot && activeShot.getAttribute('data-full-src')) {
          return {
            src: activeShot.getAttribute('data-full-src'),
            srcset: activeShot.getAttribute('data-full-srcset') || '',
            alt: activeShot.getAttribute('aria-label') || '',
          };
        }
        var fallbackImage = heroFront || heroImg;
        if (!fallbackImage) return null;
        return {
          src: fallbackImage.currentSrc || fallbackImage.src,
          srcset: fallbackImage.srcset || '',
          alt: fallbackImage.alt || '',
        };
      },
      onChange: function (fn) {
        listeners.push(fn);
      },
    };
  }

  function setupFullscreen(root, gallery) {
    var overlay = root.querySelector('[data-desk-hero-fs]');
    var openBtn = root.querySelector('[data-desk-hero-fs-open]');
    var closeBtn = root.querySelector('[data-desk-hero-fs-close]');
    var fsImg = root.querySelector('[data-desk-hero-fs-img]');
    var fsStage = root.querySelector('[data-desk-hero-fs-stage]');
    var fsPrev = root.querySelector('[data-desk-hero-fs-prev]');
    var fsNext = root.querySelector('[data-desk-hero-fs-next]');
    if (!overlay || !openBtn || !fsStage) return;

    if (!fsImg) {
      fsImg = document.createElement('img');
      fsImg.className = 'desk-shot__fs-img';
      fsImg.setAttribute('data-desk-hero-fs-img', '');
      fsImg.setAttribute('decoding', 'async');
      fsImg.width = 2000;
      fsImg.height = 2000;
      fsStage.appendChild(fsImg);
    }

    /* The stage is ~96vw once the 1400px cap is lifted; without an explicit
       sizes the browser assumed 100vw anyway, but syncFullscreenMedia's
       video-poster path strips srcset, and zoom raises this to the scaled
       CSS width to pull the 2400w rendition. */
    fsImg.sizes = '96vw';
    fsImg.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    var fsCount = root.querySelector('[data-desk-hero-fs-count]');
    var fsCaption = root.querySelector('[data-desk-hero-fs-caption]');
    var fsThumbs = root.querySelector('[data-desk-hero-fs-thumbs]');
    var shotButtons = Array.prototype.slice.call(root.querySelectorAll('[data-desk-shot]'));

    var lastFocus = null;
    var open = false;
    var suppressStageClick = false;
    var fsToken = 0;
    var thumbsBuilt = false;

    /* Click-to-zoom state. Transform is translate3d + scale against
       transform-origin 0 0 — affine and stateless: a point p in the unzoomed
       image maps to p*scale + t, so anchoring the cursor is one subtraction,
       panning mutates t, and clamping is linear. baseX/Y/W/H is the img's
       viewport rect captured while untransformed. */
    var zoom = { on: false, scale: 1, tx: 0, ty: 0, baseX: 0, baseY: 0, baseW: 0, baseH: 0 };
    var finePointer =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var reduceMotionFs =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function applyZoom() {
      fsImg.style.transform = zoom.on
        ? 'translate3d(' + zoom.tx + 'px, ' + zoom.ty + 'px, 0) scale(' + zoom.scale + ')'
        : '';
    }

    /* Pin edges to the stage; center any axis that still fits. */
    function clampPan() {
      var stageRect = fsStage.getBoundingClientRect();
      var w = zoom.baseW * zoom.scale;
      var h = zoom.baseH * zoom.scale;
      var maxX = stageRect.left - zoom.baseX;
      var minX = stageRect.right - zoom.baseX - w;
      var maxY = stageRect.top - zoom.baseY;
      var minY = stageRect.bottom - zoom.baseY - h;
      if (w <= stageRect.width) zoom.tx = maxX + (stageRect.width - w) / 2;
      else zoom.tx = Math.min(maxX, Math.max(minX, zoom.tx));
      if (h <= stageRect.height) zoom.ty = maxY + (stageRect.height - h) / 2;
      else zoom.ty = Math.min(maxY, Math.max(minY, zoom.ty));
    }

    function resetZoom() {
      if (!zoom.on) return;
      zoom.on = false;
      zoom.scale = 1;
      zoom.tx = 0;
      zoom.ty = 0;
      overlay.classList.remove('is-zoomed');
      fsImg.style.transition = '';
      applyZoom();
    }

    function zoomIn(event) {
      var rect = fsImg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      zoom.baseX = rect.left;
      zoom.baseY = rect.top;
      zoom.baseW = rect.width;
      zoom.baseH = rect.height;
      /* Natural resolution, at least 2x fit, capped 4x. */
      var naturalScale = fsImg.naturalWidth ? fsImg.naturalWidth / rect.width : 2;
      zoom.scale = Math.min(4, Math.max(2, naturalScale));
      var px = event.clientX - rect.left;
      var py = event.clientY - rect.top;
      zoom.tx = (1 - zoom.scale) * px;
      zoom.ty = (1 - zoom.scale) * py;
      zoom.on = true;
      clampPan();
      overlay.classList.add('is-zoomed');
      /* Sharper srcset candidate for the enlarged CSS size; browsers only
         ever upgrade, and photo-change/close restore 96vw. */
      fsImg.sizes = Math.round(zoom.scale * rect.width) + 'px';
      if (!reduceMotionFs) {
        fsImg.style.transition = 'transform 200ms ease';
        window.setTimeout(function () {
          fsImg.style.transition = '';
        }, 240);
      }
      applyZoom();
    }

    function stripFsLabel(label) {
      if (!label) return '';
      var cut = label.indexOf(' — image ');
      return cut > 0 ? label.slice(0, cut) : label;
    }

    function updateFsChrome() {
      if (!gallery) return;
      var current = gallery.getIndex();
      var visibleTotal = gallery.getVisibleCount ? gallery.getVisibleCount() : gallery.count;
      var visiblePos = gallery.getVisiblePosition ? gallery.getVisiblePosition() : current;
      if (fsCount) fsCount.textContent = String(visiblePos + 1) + ' / ' + String(visibleTotal);
      if (fsCaption) {
        var activeShot = shotButtons[current];
        fsCaption.textContent = activeShot
          ? stripFsLabel(activeShot.getAttribute('aria-label'))
          : '';
      }
      if (!fsThumbs) return;
      var thumbs = fsThumbs.children;
      for (var i = 0; i < thumbs.length; i += 1) {
        thumbs[i].hidden = !!(gallery.isShotVisible && !gallery.isShotVisible(i));
        var on = i === current;
        thumbs[i].classList.toggle('is-active', on);
        if (on) thumbs[i].setAttribute('aria-current', 'true');
        else thumbs[i].removeAttribute('aria-current');
      }
      var activeThumb = thumbs[current];
      if (!activeThumb) return;
      /* Same horizontal-only container follow as the strip. */
      var railLeft = fsThumbs.scrollLeft;
      var thumbLeft = activeThumb.offsetLeft;
      var thumbRight = thumbLeft + activeThumb.offsetWidth;
      var railRight = railLeft + fsThumbs.clientWidth;
      var target = railLeft;
      if (thumbLeft < railLeft) target = thumbLeft;
      else if (thumbRight > railRight) target = thumbRight - fsThumbs.clientWidth;
      if (target === railLeft) return;
      if (typeof fsThumbs.scrollTo === 'function') {
        fsThumbs.scrollTo({ left: target, behavior: reduceMotionFs ? 'auto' : 'smooth' });
      } else {
        fsThumbs.scrollLeft = target;
      }
    }

    /* Built lazily on first open from the REAL strip tiles (index-aligned with
       the gallery), so video/custom-hero/PNG ordering logic never duplicates. */
    function buildThumbs() {
      if (thumbsBuilt || !fsThumbs || !gallery || gallery.count < 2) return;
      thumbsBuilt = true;
      shotButtons.forEach(function (shot, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'desk-shot__fs-thumb';
        btn.setAttribute('data-desk-fs-thumb', '');
        btn.setAttribute('aria-label', shot.getAttribute('aria-label') || 'Photo ' + (i + 1));
        var img = document.createElement('img');
        img.src = shot.getAttribute('data-thumb-src') || '';
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        btn.appendChild(img);
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          gallery.activate(i, { fullscreen: true });
        });
        fsThumbs.appendChild(btn);
      });
    }

    function syncFullscreenMedia() {
      var activeMedia =
        gallery && gallery.getFullscreenMedia ? gallery.getFullscreenMedia() : null;
      if (!activeMedia) return;
      var token = ++fsToken;
      /* Attribute compare too: .src/.currentSrc are resolved absolute URLs
         while data-full-src may be protocol-relative. */
      if (
        fsImg.getAttribute('src') === activeMedia.src ||
        fsImg.src === activeMedia.src ||
        fsImg.currentSrc === activeMedia.src
      ) {
        fsImg.alt = activeMedia.alt || '';
        return;
      }
      /* First open: paint the hero's already-decoded frame instantly so the
         stage is never blank while the full asset decodes. */
      if (!fsImg.currentSrc && !fsImg.getAttribute('src')) {
        var heroFrontImg = root.querySelector('.desk-shot__hero-media img.is-front');
        if (heroFrontImg && heroFrontImg.currentSrc) {
          fsImg.removeAttribute('srcset');
          fsImg.src = heroFrontImg.currentSrc;
        }
      }
      /* Decode-then-swap (same token pattern as paintHero): rapid arrow nav
         only ever commits the newest request. */
      var probe = new Image();
      probe.decoding = 'async';
      probe.sizes = fsImg.sizes || '96vw';
      if (activeMedia.srcset) probe.srcset = activeMedia.srcset;
      probe.src = activeMedia.src;
      var commit = function () {
        if (token !== fsToken) return;
        if (activeMedia.srcset) fsImg.srcset = activeMedia.srcset;
        else fsImg.removeAttribute('srcset');
        fsImg.sizes = '96vw';
        fsImg.src = activeMedia.src;
        fsImg.alt = activeMedia.alt || '';
      };
      if (typeof probe.decode === 'function') {
        probe.decode().then(commit).catch(commit);
      } else if (probe.complete) {
        commit();
      } else {
        probe.onload = commit;
        probe.onerror = commit;
      }
    }

    function finishClose() {
      if (!open && !overlay.classList.contains('is-open')) return;
      open = false;
      resetZoom();
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('desk-shot-fs-open');
      openBtn.setAttribute('aria-expanded', 'false');
      if (gallery && gallery.resumeTransient) gallery.resumeTransient();
      var focusTarget = lastFocus;
      if (gallery && gallery.isCleanTarget && gallery.isCleanTarget(focusTarget)) {
        focusTarget = root.querySelector('[data-desk-shot].is-active') || openBtn;
      }
      if (focusTarget && typeof focusTarget.focus === 'function') {
        window.requestAnimationFrame(function () {
          try {
            focusTarget.focus();
          } catch (e) {
            /* ignore */
          }
        });
      }
    }

    function openDialog() {
      if (open || overlay.open) return;
      lastFocus = document.activeElement;
      syncFullscreenMedia();
      buildThumbs();
      updateFsChrome();
      if (gallery && gallery.pauseTransient) gallery.pauseTransient();
      document.documentElement.classList.add('desk-shot-fs-open');
      overlay.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      open = true;
      try {
        overlay.showModal();
      } catch (e) {
        overlay.setAttribute('open', '');
      }
      window.requestAnimationFrame(function () {
        if (closeBtn) closeBtn.focus();
      });
    }

    function closeDialog() {
      if (!open && !overlay.open) return;
      if (typeof overlay.close === 'function' && overlay.open) {
        overlay.close();
      } else {
        overlay.removeAttribute('open');
        finishClose();
      }
    }

    openBtn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      openDialog();
    });

    // Tapping/clicking the hero itself opens fullscreen — every viewport
    // (Aaron 2026-08-19: on desktop "you should be able to click a photo and
    // a thing pop out where you can see the full photo and then scroll
    // through them"; previously this was gated to <=749px and desktop only
    // had the small corner button). Distance + duration gates keep the
    // gallery swipe/drag gesture working — only a clean primary-button tap
    // opens. On non-mobile widths a click on a fronted VIDEO layer is left
    // alone (the dialog can only show its poster still, which reads as a
    // bug); mobile keeps its original tap-anywhere behaviour.
    var tapMedia = root.querySelector('[data-desk-hero-media]');
    if (tapMedia) {
      // FIX 2026-08-24: this never fired on multi-photo products. The gallery
      // swipe handler pointer-captures on .desk-shot__hero at pointerdown,
      // which retargets the matching pointerup AWAY from this media div — so
      // the release listener must live on the capture target itself. It also
      // receives the uncaptured single-photo case by plain bubbling.
      // pointerdown stays on the media so only gestures that START on the
      // photo can become taps; pointerId correlates down/up.
      var tapSurface = tapMedia.closest('.desk-shot__hero') || tapMedia;
      var tapX = 0;
      var tapY = 0;
      var tapT = 0;
      var tapPointerId = null;
      tapMedia.addEventListener(
        'pointerdown',
        function (event) {
          if (event.isPrimary === false) return;
          tapPointerId = event.pointerId;
          tapX = event.clientX;
          tapY = event.clientY;
          tapT = Date.now();
        },
        { passive: true }
      );
      tapSurface.addEventListener('pointerup', function (event) {
        if (tapPointerId === null || event.pointerId !== tapPointerId) return;
        tapPointerId = null;
        if (typeof event.button === 'number' && event.button !== 0) return;
        if (!window.matchMedia('(max-width: 749px)').matches) {
          // Layers carry pointer-events:none, so the target is always the
          // hero-media div — detect a fronted video by its .is-front class.
          if (tapMedia.querySelector('video.is-front')) return;
        }
        if (Date.now() - tapT > 400) return;
        if (Math.abs(event.clientX - tapX) > 10 || Math.abs(event.clientY - tapY) > 10) return;
        openDialog();
      });
      tapSurface.addEventListener('pointercancel', function (event) {
        if (tapPointerId !== null && event.pointerId === tapPointerId) tapPointerId = null;
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        closeDialog();
      });
    }

    overlay.addEventListener('click', function (event) {
      if (event.target !== overlay && event.target !== fsStage) return;
      if (suppressStageClick) {
        suppressStageClick = false;
        return;
      }
      closeDialog();
    });

    overlay.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDialog();
    });

    overlay.addEventListener('close', finishClose);

    if (fsPrev && gallery && gallery.count > 1) {
      fsPrev.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        gallery.activate(gallery.getIndex() - 1, { fullscreen: true });
      });
    }

    if (fsNext && gallery && gallery.count > 1) {
      fsNext.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        gallery.activate(gallery.getIndex() + 1, { fullscreen: true });
      });
    }

    if (gallery && gallery.onChange) {
      gallery.onChange(function () {
        if (!open) return;
        resetZoom();
        syncFullscreenMedia();
        updateFsChrome();
      });
    }

    overlay.addEventListener('keydown', function (event) {
      if (!open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
      } else if (event.key === 'Tab') {
        /* Every button in the dialog, DOM order: close -> prev -> next ->
           thumb rail. Same visibility filter as before. */
        var focusableControls = Array.prototype.slice.call(
          overlay.querySelectorAll('button')
        ).filter(function (control) {
          return (
            control &&
            !control.hidden &&
            !control.disabled &&
            control.getAttribute('aria-hidden') !== 'true' &&
            control.getClientRects().length > 0
          );
        });
        if (focusableControls.length) {
          event.preventDefault();
          var activeControl = focusableControls.indexOf(document.activeElement);
          var direction = event.shiftKey ? -1 : 1;
          var nextControl =
            activeControl < 0
              ? 0
              : (activeControl + direction + focusableControls.length) % focusableControls.length;
          focusableControls[nextControl].focus();
        }
      } else if (event.key === 'ArrowLeft' && gallery && gallery.count > 1) {
        event.preventDefault();
        gallery.activate(gallery.getIndex() - 1, { fullscreen: true });
      } else if (event.key === 'ArrowRight' && gallery && gallery.count > 1) {
        event.preventDefault();
        gallery.activate(gallery.getIndex() + 1, { fullscreen: true });
      }
    });

    // Swipe in fullscreen
    var startX = 0;
    var startY = 0;
    var tracking = false;
    overlay.addEventListener(
      'pointerdown',
      function (event) {
        if (event.target.closest && event.target.closest('button')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        suppressStageClick = false;
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      },
      { passive: true }
    );
    overlay.addEventListener('pointerup', function (event) {
      if (!tracking) return;
      tracking = false;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        suppressStageClick = true;
        window.setTimeout(function () {
          suppressStageClick = false;
        }, 0);
      }
      /* While zoomed, horizontal movement is pan, never photo change. */
      if (zoom.on) return;
      if (!gallery || gallery.count < 2) return;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) gallery.activate(gallery.getIndex() + 1, { fullscreen: true });
      else gallery.activate(gallery.getIndex() - 1, { fullscreen: true });
    });
    overlay.addEventListener('pointercancel', function () {
      tracking = false;
    });

    /* —— Click-to-zoom + pan ——
       Click toggles zoom (cursor-anchored). Fine pointers pan by cursor
       position over the stage; coarse pointers drag-pan with capture. The
       shared suppressStageClick latch keeps a pan release from reading as a
       toggle-click or a backdrop close. */
    fsImg.addEventListener('click', function (event) {
      if (suppressStageClick) return;
      if (zoom.on) resetZoom();
      else zoomIn(event);
    });

    var panFrame = null;
    overlay.addEventListener('pointermove', function (event) {
      if (!zoom.on || !finePointer || panPointerId !== null) return;
      var clientX = event.clientX;
      var clientY = event.clientY;
      if (panFrame !== null) return;
      panFrame = window.requestAnimationFrame(function () {
        panFrame = null;
        if (!zoom.on) return;
        var stageRect = fsStage.getBoundingClientRect();
        if (!stageRect.width || !stageRect.height) return;
        var w = zoom.baseW * zoom.scale;
        var h = zoom.baseH * zoom.scale;
        var maxX = stageRect.left - zoom.baseX;
        var minX = stageRect.right - zoom.baseX - w;
        var maxY = stageRect.top - zoom.baseY;
        var minY = stageRect.bottom - zoom.baseY - h;
        var rx = Math.min(1, Math.max(0, (clientX - stageRect.left) / stageRect.width));
        var ry = Math.min(1, Math.max(0, (clientY - stageRect.top) / stageRect.height));
        if (w > stageRect.width) zoom.tx = maxX + (minX - maxX) * rx;
        if (h > stageRect.height) zoom.ty = maxY + (minY - maxY) * ry;
        applyZoom();
      });
    });

    var panPointerId = null;
    var panLastX = 0;
    var panLastY = 0;
    var panMoved = 0;
    fsImg.addEventListener('pointerdown', function (event) {
      if (!zoom.on || finePointer) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      panPointerId = event.pointerId;
      panLastX = event.clientX;
      panLastY = event.clientY;
      panMoved = 0;
      try {
        fsImg.setPointerCapture(event.pointerId);
      } catch (e) {
        /* ignore */
      }
    });
    fsImg.addEventListener('pointermove', function (event) {
      if (panPointerId === null || event.pointerId !== panPointerId) return;
      var dx = event.clientX - panLastX;
      var dy = event.clientY - panLastY;
      panLastX = event.clientX;
      panLastY = event.clientY;
      panMoved += Math.abs(dx) + Math.abs(dy);
      zoom.tx += dx;
      zoom.ty += dy;
      clampPan();
      applyZoom();
    });
    function endFsPan(event) {
      if (panPointerId === null || (event && event.pointerId !== panPointerId)) return;
      panPointerId = null;
      if (panMoved > 10) {
        suppressStageClick = true;
        window.setTimeout(function () {
          suppressStageClick = false;
        }, 0);
      }
    }
    fsImg.addEventListener('pointerup', endFsPan);
    fsImg.addEventListener('pointercancel', endFsPan);

    /* The zoom base rect is captured at zoom-in; a viewport resize while
       zoomed invalidates it — just reset. */
    window.addEventListener('resize', function () {
      if (open && zoom.on) resetZoom();
    });
  }

  /* "N / M" chip on the hero. NOT part of clean-still hiding: once the
     shopper is browsing photos this is exactly the information they need. */
  function setupHeroCounter(root, gallery) {
    var counter = root.querySelector('[data-desk-hero-count]');
    if (!counter || !gallery) return;
    if (gallery.count < 2) {
      counter.hidden = true;
      return;
    }
    var current = counter.querySelector('[data-desk-hero-count-current]');
    var total = counter.querySelector('[data-desk-hero-count-total]');
    function paint() {
      var visibleTotal = gallery.getVisibleCount ? gallery.getVisibleCount() : gallery.count;
      var visiblePos = gallery.getVisiblePosition ? gallery.getVisiblePosition() : gallery.getIndex();
      counter.hidden = visibleTotal < 2;
      if (total) total.textContent = String(visibleTotal);
      if (current) current.textContent = String(visiblePos + 1);
    }
    /* The gallery's initial activate() fired before this subscription. */
    paint();
    gallery.onChange(paint);
  }

  /* Strip pager: arrow buttons page the thumbnail track; is-at-start/is-at-end
     on the wrapper drive the edge fades. Buttons stay hidden unless the track
     actually overflows. */
  function setupShotsNav(root, gallery) {
    var wrap = root.querySelector('.desk-shot__shots');
    var track = root.querySelector('[data-desk-shots-track]');
    var prev = root.querySelector('[data-desk-shots-prev]');
    var next = root.querySelector('[data-desk-shots-next]');
    if (!wrap || !track || !prev || !next) return;
    var tiles = Array.prototype.slice.call(track.querySelectorAll('[data-desk-shot]'));
    if (tiles.length < 2) return;
    var reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var navFrame = null;
    function updateNav() {
      navFrame = null;
      var overflow = track.scrollWidth > track.clientWidth + 1;
      prev.hidden = !overflow;
      next.hidden = !overflow;
      wrap.classList.toggle('has-overflow', overflow);
      if (!overflow) return;
      var maxLeft = track.scrollWidth - track.clientWidth;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft >= maxLeft - 1;
      prev.disabled = atStart;
      next.disabled = atEnd;
      wrap.classList.toggle('is-at-start', atStart);
      wrap.classList.toggle('is-at-end', atEnd);
    }
    function requestNavUpdate() {
      if (navFrame !== null) return;
      navFrame = window.requestAnimationFrame(updateNav);
    }
    function pageBy(dir) {
      var probeTile = null;
      for (var t = 0; t < tiles.length; t += 1) {
        if (tiles[t].offsetWidth > 0) {
          probeTile = tiles[t];
          break;
        }
      }
      var tileWidth = (probeTile && probeTile.offsetWidth + 2) || 200; /* 2px track gap */
      var perPage = Math.max(1, Math.floor(track.clientWidth / tileWidth));
      var maxLeft = track.scrollWidth - track.clientWidth;
      var nextLeft = Math.max(0, Math.min(maxLeft, track.scrollLeft + dir * perPage * tileWidth));
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: nextLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft = nextLeft;
      }
    }
    prev.addEventListener('click', function () {
      pageBy(-1);
    });
    next.addEventListener('click', function () {
      pageBy(1);
    });
    track.addEventListener('scroll', requestNavUpdate, { passive: true });
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(updateNav, 150);
    });
    /* Tile widths settle after images/fonts. */
    window.addEventListener('load', updateNav);
    updateNav();
    /* Colour filtering hides tiles and shrinks scrollWidth; re-check overflow
       whenever the gallery announces a change. */
    if (gallery && gallery.onChange) gallery.onChange(requestNavUpdate);
  }

  /* Page-level arrow keys: photos step whenever fullscreen is closed and the
     shopper is not in a form control. Scoped to body/root targets, so focus
     inside Judge.me, the header, or a drawer (all outside the section root)
     never triggers photo changes. */
  function setupHotkeys(root, gallery) {
    if (!gallery || gallery.count < 2) return;
    var overlay = root.querySelector('[data-desk-hero-fs]');
    document.addEventListener('keydown', function (event) {
      /* Stale after shopify:section:load re-renders the section. */
      if (!document.contains(root)) return;
      if (event.defaultPrevented) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      /* The dialog owns its own arrow handling. */
      if (overlay && overlay.open) return;
      var target = event.target;
      var inScope =
        target === document.body ||
        target === document.documentElement ||
        (target && root.contains(target));
      if (!inScope) return;
      if (target && target.closest) {
        if (
          target.closest(
            'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
          )
        ) {
          return;
        }
      }
      if (target && target.isContentEditable) return;
      event.preventDefault();
      gallery.activate(gallery.getIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });
  }

  function setupQty(root) {
    var inputs = Array.from(root.querySelectorAll('[data-desk-qty-input]'));
    var decs = Array.from(root.querySelectorAll('[data-desk-qty-dec]'));
    var incs = Array.from(root.querySelectorAll('[data-desk-qty-inc]'));
    if (!inputs.length) return;

    var qty = Math.max(1, parseInt(inputs[0].value, 10) || 1);

    function sync(next) {
      qty = Math.min(9, Math.max(1, next));
      inputs.forEach(function (input) {
        input.value = String(qty);
      });
      root.querySelectorAll('[data-desk-qty-display]').forEach(function (el) {
        el.textContent = String(qty);
      });
    }

    decs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        sync(qty - 1);
      });
    });
    incs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        sync(qty + 1);
      });
    });
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        sync(parseInt(input.value, 10) || 1);
      });
    });

    sync(qty);
  }

  function enhanceBundleDisclosure(root, widget) {
    var previous = widget ? widget.previousElementSibling : null;
    if (
      !widget ||
      (widget.getAttribute('data-desk-bundle-panel') === 'true' &&
        previous &&
        previous.hasAttribute('data-desk-bundle-toggle'))
    ) {
      return;
    }

    var title = widget.querySelector('.lbtu-widget-title');
    var label = title ? (title.textContent || '').replace(/\s+/g, ' ').trim() : '';
    var parent = widget.parentNode;
    if (!label || !parent) return;

    var existingToggle = parent.querySelector('[data-desk-bundle-toggle]');
    if (existingToggle) existingToggle.remove();

    if (!widget.id) {
      widget.id = 'DeskBundlePanel-' + (root.id || 'Product').replace(/[^A-Za-z0-9_-]/g, '-');
    }

    var toggle = document.createElement('button');
    var storedState = bundleDisclosureExpanded;
    var expanded = typeof storedState === 'boolean'
      ? storedState
      : !(window.matchMedia && window.matchMedia('(max-width: 989px)').matches);

    toggle.type = 'button';
    toggle.id = widget.id + '-toggle';
    toggle.className = 'desk-shot__bundle-toggle';
    toggle.textContent = label;
    toggle.setAttribute('data-desk-bundle-toggle', '');
    toggle.setAttribute('aria-controls', widget.id);
    toggle.setAttribute('aria-expanded', String(expanded));

    widget.setAttribute('data-desk-bundle-panel', 'true');
    widget.setAttribute('role', 'region');
    widget.setAttribute('aria-labelledby', toggle.id);
    widget.hidden = !expanded;

    toggle.addEventListener('click', function () {
      var nextExpanded = toggle.getAttribute('aria-expanded') !== 'true';
      bundleDisclosureExpanded = nextExpanded;
      toggle.setAttribute('aria-expanded', String(nextExpanded));
      widget.hidden = !nextExpanded;
    });

    parent.insertBefore(toggle, widget);
  }

  function setupBundleDisclosure(root) {
    if (!('MutationObserver' in window)) return;

    function enhance() {
      root.querySelectorAll('.lbtu-embed-volumeDiscount').forEach(function (widget) {
        enhanceBundleDisclosure(root, widget);
      });
    }

    enhance();
    new MutationObserver(enhance).observe(root, { childList: true, subtree: true });
  }

  /* Specs-panel mat rise. The CSS used to drive this with a scroll-linked
     animation-timeline: view(), which re-composites the filtered PNG on every
     scroll frame; one IO hit toggling a class lets the transition run once on
     the compositor instead. .js-rise gates the offset start pose so no-JS /
     failed-JS visitors keep the static final pose. */
  function setupEdgeRise(root) {
    var media = root.querySelector('.desk-shot__edge-media');
    var img = media ? media.querySelector('.desk-shot__edge-img--float') : null;
    if (!media || !img || !('IntersectionObserver' in window)) return;
    var reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    media.classList.add('js-rise');
    new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          media.classList.add('is-inview');
          observer.unobserve(media);
        });
      },
      { threshold: 0.35 }
    ).observe(media);
  }

  function setupSticky(root) {
    var bar = root.querySelector('[data-desk-sticky]');
    var heroSentinel = root.querySelector('[data-desk-sticky-sentinel]');
    var mobileSentinel = root.querySelector('[data-desk-sticky-sentinel-mobile]');
    /* The purchase band sits BELOW the shot strip on <=989px (Trey
       2026-09-01), so the band's own end marker is the right trigger at every
       width: the bar appears only once the static CTA has scrolled past. The
       strip marker remains a layout fallback if a template drops the band. */
    var sentinels = [heroSentinel, mobileSentinel].filter(Boolean);
    var sentinel = null;
    for (var s = 0; s < sentinels.length; s += 1) {
      if (sentinels[s].offsetParent !== null) {
        sentinel = sentinels[s];
        break;
      }
    }
    if (!bar) return;

    setupBuyMirror(root, bar);

    if (!sentinel || !('IntersectionObserver' in window)) {
      bar.classList.add('is-visible');
      return;
    }

    /* One reason the buy controls can be unavailable: the hero buy block has
       scrolled out of view. The gallery used to hide that block on photo
       changes and announce it via desk:copy-hidden, which this bar mirrored —
       it no longer does, so the sentinel is the only trigger again. */
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* Show only once the purchase band has been scrolled PAST. The huge
             bottom rootMargin makes "intersecting" mean "at or below the
             viewport top", so !isIntersecting is exactly "scrolled past" — and
             the state flips whenever the sentinel crosses the top edge, even
             on instant jumps that skip the viewport in a single frame. */
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { root: null, threshold: 0, rootMargin: '0px 0px 100000px 0px' }
    );

    observer.observe(sentinel);
  }

  /* Mirrors the hidden sticky form's state onto the on-screen hero button:
     loading/disabled classes, and surfacing add-to-cart errors that Dawn
     writes into the (possibly off-screen) sticky bar. */
  function setupBuyMirror(root, bar) {
    var stickyBtn = bar ? bar.querySelector('[type="submit"]') : null;
    var purchaseButtons = stickyBtn
      ? Array.from(root.querySelectorAll('[data-desk-add]')).filter(function (button) {
          return button !== stickyBtn;
        })
      : [];
    var errorWrap = root.querySelector('.product-form__error-message-wrapper');
    if (!stickyBtn) return;

    if (purchaseButtons.length && 'MutationObserver' in window) {
      new MutationObserver(function () {
        purchaseButtons.forEach(function (button) {
          button.classList.toggle('loading', stickyBtn.classList.contains('loading'));
          if (stickyBtn.getAttribute('aria-disabled') === 'true') {
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-disabled');
          }
        });
      }).observe(stickyBtn, { attributes: true, attributeFilter: ['class', 'aria-disabled'] });

      var spin = stickyBtn.querySelector('.loading__spinner');
      purchaseButtons.forEach(function (button) {
        if (spin && !button.querySelector('.loading__spinner')) button.appendChild(spin.cloneNode(true));
      });
    }

    if (errorWrap && 'MutationObserver' in window) {
      new MutationObserver(function () {
        var hidden = errorWrap.hidden || errorWrap.getAttribute('hidden') !== null;
        if (!hidden && bar && !bar.classList.contains('is-visible')) {
          /* The error just landed inside a hidden bar: show the bar so the
             shopper actually sees why nothing was added. */
          bar.classList.add('is-visible');
        }
      }).observe(errorWrap, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });
    }
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var numeric = Number(cents);
    if (!Number.isFinite(numeric)) numeric = 0;
    var negative = numeric < 0 ? '-' : '';
    var fixed = (Math.abs(numeric) / 100).toFixed(precision);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return negative + parts[0] + (parts[1] ? decimal + parts[1] : '');
  }

  function formatVariantMoney(root, cents) {
    var format = root.getAttribute('data-money-format') || '${{amount}}';
    var token = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!token) return formatWithDelimiters(cents, 2, ',', '.');

    var formatted;
    switch (token[1]) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        formatted = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(token[0], formatted);
  }

  function renderPriceMarkup(price, value) {
    if (!price) return;
    var text = String(value || '').trim();
    var parts = text.match(/^(.*?)(\d(?:[\d.,'’]|\s(?=\d))*\d?)(\s*[^\d]*)$/);
    price.textContent = '';

    if (!parts) {
      price.textContent = text;
      return;
    }

    var prefix = document.createElement('span');
    prefix.className = 'desk-shot__price-prefix';
    prefix.textContent = parts[1];

    var amount = document.createElement('span');
    amount.className = 'desk-shot__price-amount';
    amount.textContent = parts[2];

    var suffix = document.createElement('span');
    suffix.className = 'desk-shot__price-suffix';
    suffix.textContent = parts[3];

    price.appendChild(prefix);
    price.appendChild(amount);
    price.appendChild(suffix);
  }

  function setupPriceMarkup(root) {
    root.querySelectorAll('[data-desk-price]').forEach(function (price) {
      renderPriceMarkup(price, price.textContent);
    });
  }

  function renderVariantState(root, variant) {
    var addLabel = root.getAttribute('data-label-add') || 'Add to cart';
    var soldOutLabel = root.getAttribute('data-label-sold-out') || 'Sold out';
    var unavailableLabel = root.getAttribute('data-label-unavailable') || 'Unavailable';
    var valid = !!variant;
    var available = valid && variant.available === true;
    var state = !valid ? 'unavailable' : available ? 'available' : 'sold-out';
    var label = !valid ? unavailableLabel : available ? addLabel : soldOutLabel;

    root.setAttribute('data-variant-state', state);
    if (valid) root.setAttribute('data-current-variant-id', String(variant.id));
    else root.removeAttribute('data-current-variant-id');

    root.querySelectorAll('input.product-variant-id').forEach(function (input) {
      var nextValue = valid ? String(variant.id) : '';
      var changed = input.value !== nextValue;
      input.value = nextValue;
      input.disabled = !available;
      if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    root.querySelectorAll('[data-desk-price]').forEach(function (price) {
      renderPriceMarkup(price, valid ? formatVariantMoney(root, variant.price) : unavailableLabel);
    });

    root.querySelectorAll('[data-desk-add]').forEach(function (button) {
      button.disabled = !available;
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
      button.setAttribute('data-variant-state', state);
      var text = button.querySelector('[data-desk-cta-label]');
      if (text) text.textContent = label;
    });

    root.querySelectorAll('[data-desk-option]').forEach(function (select) {
      if (valid) select.removeAttribute('aria-invalid');
      else select.setAttribute('aria-invalid', 'true');
    });

    root.querySelectorAll('[data-desk-availability]').forEach(function (availability) {
      availability.hidden = available;
      availability.textContent = available ? '' : label;
    });

    var dynamicCheckout = root.querySelector('[data-desk-dynamic-checkout]');
    if (dynamicCheckout) {
      dynamicCheckout.hidden = !available;
      dynamicCheckout.setAttribute('aria-hidden', available ? 'false' : 'true');
      if ('inert' in dynamicCheckout) dynamicCheckout.inert = !available;
    }

    if (window.history && window.history.replaceState) {
      try {
        var url = new URL(window.location.href);
        if (valid) url.searchParams.set('variant', String(variant.id));
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {
        /* Keep purchase controls correct even when history is unavailable. */
      }
    }
  }

  function setupVariantSelects(root, gallery) {
    var selects = Array.prototype.slice.call(root.querySelectorAll('[data-desk-option]'));
    var dataEl = root.querySelector('[data-desk-variants]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    function currentVariant() {
      if (!selects.length) {
        var currentId = root.getAttribute('data-current-variant-id');
        return variants.find(function (variant) {
          return String(variant.id) === String(currentId);
        }) || variants[0];
      }

      var chosen = selects.map(function (select) {
        return select.value;
      });
      return variants.find(function (variant) {
        return chosen.every(function (value, optionIndex) {
          return variant.options[optionIndex] === value;
        });
      }) || null;
    }

    function syncPills() {
      root.querySelectorAll('[data-desk-pill-group]').forEach(function (group) {
        var select = document.getElementById(group.getAttribute('data-desk-pill-for') || '');
        if (!select) return;
        group.querySelectorAll('[data-desk-pill]').forEach(function (pill) {
          pill.classList.toggle('is-active', pill.getAttribute('data-value') === select.value);
        });
      });
    }

    function syncHeroToVariant(variant) {
      if (!gallery || !variant || !variant.featured_image || !variant.featured_image.src) return;
      var path = String(variant.featured_image.src).split('?')[0];
      var shotEls = Array.prototype.slice.call(root.querySelectorAll('[data-desk-shot]'));
      for (var i = 0; i < shotEls.length; i += 1) {
        var full = shotEls[i].getAttribute('data-full-src') || '';
        if (full && full.split('?')[0] === path) {
          if (gallery.getIndex() !== i) gallery.activate(i, { silent: true });
          return;
        }
      }
    }

    function syncGalleryFilter(variant) {
      if (!gallery || !gallery.setColorFilter) return;
      var token = '';
      for (var i = 0; i < selects.length; i += 1) {
        var optName = (selects[i].getAttribute('name') || '').toLowerCase();
        if (optName === 'options[color]' || optName === 'options[colour]') {
          token = String(selects[i].value || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-'); /* 'Off White' -> 'off-white' */
          break;
        }
      }
      gallery.setColorFilter(token || null, variant ? String(variant.id) : '');
    }

    function apply(evt) {
      var variant = currentVariant();
      renderVariantState(root, variant);
      syncPills();
      /* Filter runs on EVERY apply, init included, so the strip/hero only
         offer this colour's shots. Hero-to-variant-image runs AFTER the
         filter and only on a real change — the init call must not steal the
         lead from the Liquid-chosen video/primary shot. */
      syncGalleryFilter(variant);
      if (evt) syncHeroToVariant(variant);
    }

    root.querySelectorAll('[data-desk-pill]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        var group = pill.closest('[data-desk-pill-group]');
        var select = group
          ? document.getElementById(group.getAttribute('data-desk-pill-for') || '')
          : null;
        var value = pill.getAttribute('data-value');
        if (!select || select.value === value) return;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    selects.forEach(function (select) {
      select.addEventListener('change', apply);
    });
    apply();
  }

  function ensureJudgeMeAnchor() {
    // Rating link targets #judgeme_product_reviews. Judge.me's widget usually
    // owns that id; if the app block mounts without it, wire the first widget.
    if (document.getElementById('judgeme_product_reviews')) return;
    var widget = document.querySelector('.jdgm-rev-widg, .jdgm-widget.jdgm-review-widget, [data-id].jdgm-widget');
    if (widget) widget.id = 'judgeme_product_reviews';
  }

  function collapseJudgeMeReviews() {
    // Judge.me paints its list asynchronously and repaints on sort/filter, so
    // this is idempotent and re-run on a short schedule rather than once.
    var list = document.querySelector('.jdgm-rev-widg__reviews');
    if (!list) return;

    var cards = list.querySelectorAll('.jdgm-rev');
    // Visible counts live in CSS (3 desktop / 2 mobile); 3 is the smaller
    // threshold to clear before a toggle is worth showing at all.
    if (cards.length <= 2) {
      list.removeAttribute('data-ds-reviews-collapsed');
      var stale = list.parentNode.querySelector('.ds-reviews-more');
      if (stale) stale.parentNode.removeChild(stale);
      return;
    }

    cards.forEach(function (card, i) {
      card.setAttribute('data-ds-review-index', String(i));
    });

    if (list.parentNode.querySelector('.ds-reviews-more')) return;

    list.setAttribute('data-ds-reviews-collapsed', '');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ds-reviews-more';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'ds-reviews-list');
    btn.textContent = 'See more reviews';
    list.id = 'ds-reviews-list';

    btn.addEventListener('click', function () {
      var collapsed = list.hasAttribute('data-ds-reviews-collapsed');
      if (collapsed) {
        list.removeAttribute('data-ds-reviews-collapsed');
        btn.setAttribute('aria-expanded', 'true');
        btn.textContent = 'Show fewer reviews';
      } else {
        list.setAttribute('data-ds-reviews-collapsed', '');
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = 'See more reviews';
        list.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    });

    list.parentNode.insertBefore(btn, list.nextSibling);
  }

  function boot() {
    document.querySelectorAll('[data-desk-shot-root]').forEach(initDeskShot);
    if (document.querySelector('[data-desk-shot-root]')) {
      document.documentElement.classList.add('desk-shot-pdp');
      hideOrphanAddons();
      window.setTimeout(hideOrphanAddons, 1600);
      ensureJudgeMeAnchor();
      window.setTimeout(ensureJudgeMeAnchor, 800);
      window.setTimeout(ensureJudgeMeAnchor, 2400);
      collapseJudgeMeReviews();
      window.setTimeout(collapseJudgeMeReviews, 800);
      window.setTimeout(collapseJudgeMeReviews, 2400);
      window.setTimeout(collapseJudgeMeReviews, 5000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector('[data-desk-shot-root]')
      : null;
    if (root) {
      root.removeAttribute('data-desk-ready');
      initDeskShot(root);
    }
  });
})();
